import type { ComponentType } from 'react'
import { connect } from 'react-redux'

import type { RootState } from './store'

type AnyProps = Record<string, unknown>

type MapStateToProps<TStateProps extends AnyProps = AnyProps> = (
    state: RootState,
    ownProps?: AnyProps,
) => TStateProps

type MapDispatchToProps = AnyProps | null

/**
 * Replace base redux connect by a simple connect wrapper.
 *
 * Usage: `export default withConnectWrapper(state => ({ foo: state.bar }))(Class)`.
 */
export function withConnectWrapper<TStateProps extends AnyProps = AnyProps>(
    mapStateToProps: MapStateToProps<TStateProps>,
    mapDispatchToProps: MapDispatchToProps = null,
) {
    return function wrap(WrappedComponent: ComponentType<AnyProps>): ComponentType<AnyProps> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return connect(mapStateToProps as any, mapDispatchToProps as any)(WrappedComponent as any) as unknown as ComponentType<AnyProps>
    }
}

export interface AuthedUserProps {
    authedUser: unknown
    busyAuth: boolean
    errorAuth: string | null
}

/**
 * HOC injecting authed user related props from the auth slice.
 *
 * Replaces the legacy `@authedUserConnect()` decorator for class components.
 */
export function withAuthedUser<TProps extends AnyProps>(
    WrappedComponent: ComponentType<TProps & AuthedUserProps>,
): ComponentType<TProps> {
    const mapState = (state: RootState) => ({
        authedUser: state.auth.user,
        busyAuth: state.auth.busy,
        errorAuth: state.auth.error,
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return connect(mapState)(WrappedComponent as any) as unknown as ComponentType<TProps>
}
